import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  InputAdornment,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search, Visibility } from '@mui/icons-material';
import { jobAPI, locationAPI, positionAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ENTITY_TYPES = [
  'Insurance',
  'Logistics',
  'Real Estate',
  'Multifamily',
  'Hospitality'
];

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [positions, setPositions] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [openPositionDialog, setOpenPositionDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [newLocation, setNewLocation] = useState({ name: '', city: '', state: '', country: 'USA' });
  const [newPosition, setNewPosition] = useState({ title: '', department: '', description: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    location: '',
    position: '',
    employmentType: '',
    experienceLevel: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    entity: '',
    company: '',
    locations: [],
    position: '',
    employmentType: '',
    salaryRange: { min: '', max: '', currency: 'USD' },
    requiredSkills: '',
    experienceLevel: '',
    experience: '',
    numberOfVacancies: 1,
    isActive: true
  });
  const { isAdmin } = useAuth();

  const employmentTypes = ['Full-time', 'Part-time', 'Contract', 'Internship'];
  const experienceLevels = ['Entry Level', 'Mid Level', 'Senior Level', 'Executive'];

  useEffect(() => {
    fetchJobs();
    fetchLocations();
    fetchPositions();
    fetchCompanies();
  }, [searchTerm, filters]);

  useEffect(() => {
    if (formData.entity) {
      const filtered = companies.filter(c => c.entity === formData.entity);
      setFilteredCompanies(filtered);
      if (formData.company) {
        const currentCompany = companies.find(c => c._id === formData.company);
        if (currentCompany && currentCompany.entity !== formData.entity) {
          setFormData(prev => ({ ...prev, company: '' }));
        }
      }
    } else {
      setFilteredCompanies(companies);
    }
  }, [formData.entity, companies]);

  // Filter locations based on entity and company
  useEffect(() => {
    if (formData.entity) {
      let filtered = locations.filter(loc => loc.entity === formData.entity);
      
      // If company is selected, filter by company too
      if (formData.company) {
        filtered = filtered.filter(loc => 
          loc.company && loc.company._id === formData.company
        );
      } else {
        // If no company selected, show locations without company (entity-only)
        filtered = filtered.filter(loc => !loc.company);
      }
      
      setFilteredLocations(filtered);
      
      // Reset locations if current selections don't match filters
      if (formData.locations && formData.locations.length > 0) {
        const validLocations = formData.locations.filter(locId => {
          const loc = locations.find(l => l._id === locId);
          if (!loc) return false;
          const matchesEntity = loc.entity === formData.entity;
          const matchesCompany = formData.company 
            ? (loc.company && loc.company._id === formData.company)
            : !loc.company;
          return matchesEntity && matchesCompany;
        });
        if (validLocations.length !== formData.locations.length) {
          setFormData(prev => ({ ...prev, locations: validLocations }));
        }
      }
    } else {
      setFilteredLocations(locations);
    }
  }, [formData.entity, formData.company, locations]);

  // Filter positions based on entity and company
  useEffect(() => {
    if (formData.entity) {
      let filtered = positions.filter(pos => pos.entity === formData.entity);
      
      // If company is selected, filter by company too
      if (formData.company) {
        filtered = filtered.filter(pos => 
          pos.company && pos.company._id === formData.company
        );
      } else {
        // If no company selected, show positions without company (entity-only)
        filtered = filtered.filter(pos => !pos.company);
      }
      
      setFilteredPositions(filtered);
      
      // Reset position if current selection doesn't match filters
      if (formData.position) {
        const currentPosition = positions.find(p => p._id === formData.position);
        if (currentPosition) {
          const matchesEntity = currentPosition.entity === formData.entity;
          const matchesCompany = formData.company 
            ? (currentPosition.company && currentPosition.company._id === formData.company)
            : !currentPosition.company;
          if (!matchesEntity || !matchesCompany) {
            setFormData(prev => ({ ...prev, position: '' }));
          }
        }
      }
    } else {
      setFilteredPositions(positions);
    }
  }, [formData.entity, formData.company, positions]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchTerm,
        limit: 100,
        ...filters
      };
      const response = await jobAPI.getAll(params);
      setJobs(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch jobs', 'error');
    }
    setLoading(false);
  };

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getAll({ limit: 100, isActive: true });
      setLocations(response.data.data);
    } catch (error) {
      console.error('Failed to fetch locations');
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await positionAPI.getAll({ limit: 100, isActive: true });
      setPositions(response.data.data);
    } catch (error) {
      console.error('Failed to fetch positions');
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyAPI.getAll({ isActive: 'true' });
      setCompanies(response.data.data);
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditMode(true);
      setCurrentJob(job);
      setFormData({
        title: job.title,
        description: job.description,
        entity: job.entity || '',
        company: job.company?._id || job.company || '',
        locations: job.locations?.map(loc => loc._id || loc) || [],
        position: job.position?._id || '',
        employmentType: job.employmentType,
        salaryRange: job.salaryRange || { min: '', max: '', currency: 'USD' },
        requiredSkills: job.requiredSkills?.join(', ') || '',
        experienceLevel: job.experienceLevel,
        experience: job.experience || '',
        numberOfVacancies: job.numberOfVacancies || 1,
        isActive: job.isActive !== undefined ? job.isActive : true
      });
    } else {
      setEditMode(false);
      setCurrentJob(null);
      setFormData({
        title: '',
        description: '',
        entity: '',
        company: '',
        locations: [],
        position: '',
        employmentType: '',
        salaryRange: { min: '', max: '', currency: 'USD' },
        requiredSkills: '',
        experienceLevel: '',
        experience: '',
        numberOfVacancies: 1,
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleViewJob = (job) => {
    setCurrentJob(job);
    setOpenViewDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOpenViewDialog(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle "Add New" selection for position
    if (name === 'position' && value === '__add_new__') {
      setOpenPositionDialog(true);
      return;
    }
    
    if (name.startsWith('salary')) {
      const field = name.split('.')[1];
      setFormData({
        ...formData,
        salaryRange: { ...formData.salaryRange, [field]: value }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleLocationChange = (event) => {
    const value = event.target.value;
    
    // Handle "Add New" selection
    if (value.includes('__add_new__')) {
      setOpenLocationDialog(true);
      return;
    }
    
    // Handle "Select All"
    if (value.includes('__select_all__')) {
      if (formData.locations.length === filteredLocations.length) {
        // Deselect all
        setFormData({ ...formData, locations: [] });
      } else {
        // Select all
        setFormData({ ...formData, locations: filteredLocations.map(loc => loc._id) });
      }
      return;
    }
    
    setFormData({ ...formData, locations: typeof value === 'string' ? value.split(',') : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        employmentType: formData.employmentType || undefined,
        experienceLevel: formData.experienceLevel || undefined,
        requiredSkills: formData.requiredSkills.split(',').map(s => s.trim()).filter(s => s),
        experience: formData.experience,
        numberOfVacancies: parseInt(formData.numberOfVacancies) || 1,
        salaryRange: {
          min: parseInt(formData.salaryRange.min) || 0,
          max: parseInt(formData.salaryRange.max) || 0,
          currency: formData.salaryRange.currency
        }
      };

      if (editMode) {
        await jobAPI.update(currentJob._id, submitData);
        showSnackbar('Job updated successfully', 'success');
      } else {
        await jobAPI.create(submitData);
        showSnackbar('Job posted successfully', 'success');
      }
      handleCloseDialog();
      fetchJobs();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await jobAPI.delete(id);
        showSnackbar('Job deleted successfully', 'success');
        fetchJobs();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Delete failed', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddLocation = async () => {
    try {
      const locationData = {
        ...newLocation,
        entity: formData.entity,
        company: formData.company || null
      };
      const response = await locationAPI.create(locationData);
      showSnackbar('Location added successfully', 'success');
      setOpenLocationDialog(false);
      setNewLocation({ name: '', city: '', state: '', country: 'USA' });
      await fetchLocations();
      // Add the newly created location to selected locations
      setFormData({ ...formData, locations: [...formData.locations, response.data.data._id] });
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to add location', 'error');
    }
  };

  const handleAddPosition = async () => {
    try {
      const positionData = {
        ...newPosition,
        entity: formData.entity,
        company: formData.company || null
      };
      const response = await positionAPI.create(positionData);
      showSnackbar('Position added successfully', 'success');
      setOpenPositionDialog(false);
      setNewPosition({ title: '', department: '', description: '' });
      await fetchPositions();
      // Set the newly created position as selected
      setFormData({ ...formData, position: response.data.data._id });
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to add position', 'error');
    }
  };

  const columns = [
    { 
      field: 'title', 
      headerName: 'Job Title', 
      flex: 1, 
      minWidth: 200,
      renderCell: (params) => (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            height: '100%',
            borderLeft: `4px solid ${params.row.position?.color || '#1976d2'}`,
            paddingLeft: 1
          }}
        >
          <Typography>{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'entity',
      headerName: 'Entity',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color="primary"
          variant="outlined"
          size="small"
        />
      )
    },
    {
      field: 'company',
      headerName: 'Company',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.company?.name || 'Entity Only'
    },
    {
      field: 'locations',
      headerName: 'Locations',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.row.locations && params.row.locations.length > 0 ? (
            params.row.locations.slice(0, 2).map((loc, idx) => (
              <Chip key={idx} label={loc.name} size="small" />
            ))
          ) : (
            <Typography variant="caption" color="textSecondary">N/A</Typography>
          )}
          {params.row.locations && params.row.locations.length > 2 && (
            <Chip label={`+${params.row.locations.length - 2}`} size="small" color="primary" />
          )}
        </Box>
      )
    },
    {
      field: 'position',
      headerName: 'Position',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '2px',
              backgroundColor: params.row.position?.color || '#1976d2',
              border: '1px solid #ddd'
            }}
          />
          <Typography>{params.row.position?.title || 'N/A'}</Typography>
        </Box>
      )
    },
    {
      field: 'employmentType',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Chip label={params.value} size="small" color="primary" />
        ) : (
          <Typography variant="caption" color="textSecondary">N/A</Typography>
        )
      )
    },
    {
      field: 'experienceLevel',
      headerName: 'Experience',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Chip label={params.value} size="small" color="secondary" />
        ) : (
          <Typography variant="caption" color="textSecondary">N/A</Typography>
        )
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="info"
            onClick={() => handleViewJob(params.row)}
          >
            <Visibility />
          </IconButton>
          {isAdmin && (
            <>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleOpenDialog(params.row)}
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(params.row._id)}
              >
                <Delete />
              </IconButton>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Jobs</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Post New Job
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={filters.location}
                label="Location"
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc._id} value={loc._id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Position</InputLabel>
              <Select
                value={filters.position}
                label="Position"
                onChange={(e) => setFilters({ ...filters, position: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {positions.map((pos) => (
                  <MenuItem key={pos._id} value={pos._id}>
                    {pos.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Employment Type</InputLabel>
              <Select
                value={filters.employmentType}
                label="Employment Type"
                onChange={(e) => setFilters({ ...filters, employmentType: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {employmentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Experience Level</InputLabel>
              <Select
                value={filters.experienceLevel}
                label="Experience Level"
                onChange={(e) => setFilters({ ...filters, experienceLevel: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {experienceLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={jobs}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          getRowId={(row) => row._id}
          disableSelectionOnClick
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Job' : 'Post New Job'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  name="title"
                  label="Job Title"
                  type="text"
                  fullWidth
                  required
                  value={formData.title}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                  Job Description *
                </Typography>
                <Box sx={{ 
                  '& .quill': { 
                    backgroundColor: 'white',
                    borderRadius: '4px'
                  },
                  '& .ql-container': {
                    minHeight: '200px',
                    fontSize: '14px'
                  },
                  '& .ql-editor': {
                    minHeight: '200px'
                  }
                }}>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'indent': '-1'}, { 'indent': '+1' }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="entity"
                  label="Entity Type"
                  fullWidth
                  required
                  value={formData.entity}
                  onChange={handleChange}
                >
                  {ENTITY_TYPES.map((entity) => (
                    <MenuItem key={entity} value={entity}>
                      {entity}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="company"
                  label="Company (Optional)"
                  fullWidth
                  value={formData.company}
                  onChange={handleChange}
                  disabled={!formData.entity}
                  helperText={!formData.entity ? 'Select entity first' : 'Leave empty to post under entity only'}
                >
                  <MenuItem value="">
                    <em>None - Post under Entity only</em>
                  </MenuItem>
                  {filteredCompanies.map((company) => (
                    <MenuItem key={company._id} value={company._id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth required disabled={!formData.entity}>
                  <InputLabel>Locations</InputLabel>
                  <Select
                    multiple
                    value={formData.locations}
                    onChange={handleLocationChange}
                    input={<OutlinedInput label="Locations" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const loc = locations.find(l => l._id === value);
                          return (
                            <Chip key={value} label={loc?.name || value} size="small" />
                          );
                        })}
                      </Box>
                    )}
                  >
                    <MenuItem value="__select_all__" sx={{ fontWeight: 600, borderBottom: '1px solid #ddd' }}>
                      <Checkbox 
                        checked={formData.locations.length === filteredLocations.length && filteredLocations.length > 0}
                        indeterminate={formData.locations.length > 0 && formData.locations.length < filteredLocations.length}
                      />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontWeight: 600, borderBottom: '1px solid #ddd' }}>
                      + Add New Location
                    </MenuItem>
                    {filteredLocations.map((loc) => (
                      <MenuItem key={loc._id} value={loc._id}>
                        <Checkbox checked={formData.locations.indexOf(loc._id) > -1} />
                        <ListItemText primary={`${loc.name} - ${loc.city || 'N/A'}`} />
                      </MenuItem>
                    ))}
                  </Select>
                  {!formData.entity && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      Select entity first
                    </Typography>
                  )}
                  {formData.entity && filteredLocations.length === 0 && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      No locations found for selected entity/company
                    </Typography>
                  )}
                  {formData.entity && filteredLocations.length > 0 && (
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.5 }}>
                      {formData.locations.length} of {filteredLocations.length} location(s) selected
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  name="position"
                  label="Position"
                  fullWidth
                  required
                  value={formData.position}
                  onChange={handleChange}
                  disabled={!formData.entity}
                  helperText={
                    !formData.entity 
                      ? 'Select entity first' 
                      : filteredPositions.length === 0 
                        ? 'No positions found for selected entity/company' 
                        : `${filteredPositions.length} position(s) available`
                  }
                >
                  <MenuItem value="__add_new__" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    + Add New Position
                  </MenuItem>
                  {filteredPositions.map((pos) => (
                    <MenuItem key={pos._id} value={pos._id}>
                      {pos.title} - {pos.department || 'N/A'}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    name="employmentType"
                    value={formData.employmentType}
                    label="Employment Type"
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {employmentTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Experience Level</InputLabel>
                  <Select
                    name="experienceLevel"
                    value={formData.experienceLevel}
                    label="Experience Level"
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {experienceLevels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="experience"
                  label="Experience Required"
                  type="text"
                  fullWidth
                  value={formData.experience}
                  onChange={handleChange}
                  placeholder="e.g., 2-5 years, 3+ years, Fresher"
                  helperText="Enter experience requirement (e.g., 2-5 years)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="numberOfVacancies"
                  label="Number of Vacancies"
                  type="number"
                  fullWidth
                  required
                  value={formData.numberOfVacancies}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                  helperText="Number of open positions"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="salary.min"
                  label="Min Salary"
                  type="number"
                  fullWidth
                  value={formData.salaryRange.min}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  name="salary.max"
                  label="Max Salary"
                  type="number"
                  fullWidth
                  value={formData.salaryRange.max}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="requiredSkills"
                  label="Required Skills (comma separated)"
                  type="text"
                  fullWidth
                  value={formData.requiredSkills}
                  onChange={handleChange}
                  helperText="e.g., JavaScript, React, Node.js"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  name="isActive"
                  label="Job Status"
                  fullWidth
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Update' : 'Post Job'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* View Job Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{currentJob?.title}</DialogTitle>
        <DialogContent>
          {currentJob && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Job Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Description
                  </Typography>
                  <Box 
                    sx={{ 
                      '& p': { marginBottom: '0.5em' },
                      '& ul, & ol': { marginLeft: '1.5em', marginBottom: '0.5em' },
                      '& h1, & h2, & h3': { marginTop: '0.5em', marginBottom: '0.5em' }
                    }}
                    dangerouslySetInnerHTML={{ __html: currentJob.description }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Locations
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentJob.locations && currentJob.locations.length > 0 ? (
                      currentJob.locations.map((loc, idx) => (
                        <Chip 
                          key={idx} 
                          label={`${loc.name}${loc.city ? `, ${loc.city}` : ''}`}
                          variant="outlined"
                          size="small"
                        />
                      ))
                    ) : (
                      <Typography variant="body1" color="textSecondary">Not specified</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Position
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '3px',
                        backgroundColor: currentJob.position?.color || '#1976d2',
                        border: '1px solid #ddd'
                      }}
                    />
                    <Typography variant="body1">{currentJob.position?.title}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Number of Vacancies
                  </Typography>
                  <Chip 
                    label={`${currentJob.numberOfVacancies || 1} Position${(currentJob.numberOfVacancies || 1) > 1 ? 's' : ''}`} 
                    color="info" 
                    size="small" 
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Employment Type
                  </Typography>
                  {currentJob.employmentType ? (
                    <Chip label={currentJob.employmentType} color="primary" size="small" />
                  ) : (
                    <Typography variant="body1" color="textSecondary">Not specified</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Experience Level
                  </Typography>
                  {currentJob.experienceLevel ? (
                    <Chip label={currentJob.experienceLevel} color="secondary" size="small" />
                  ) : (
                    <Typography variant="body1" color="textSecondary">Not specified</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Experience Required
                  </Typography>
                  <Typography variant="body1">
                    {currentJob.experience || 'Not specified'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Salary Range
                  </Typography>
                  <Typography variant="body1">
                    {currentJob.salaryRange?.currency} {currentJob.salaryRange?.min?.toLocaleString()} -{' '}
                    {currentJob.salaryRange?.max?.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Required Skills
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {currentJob.requiredSkills?.map((skill, index) => (
                      <Chip key={index} label={skill} size="small" />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Location Dialog */}
      <Dialog open={openLocationDialog} onClose={() => setOpenLocationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Location</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Location Name"
                fullWidth
                required
                value={newLocation.name}
                onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                fullWidth
                required
                value={newLocation.city}
                onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="State"
                fullWidth
                required
                value={newLocation.state}
                onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Country"
                fullWidth
                value={newLocation.country}
                onChange={(e) => setNewLocation({ ...newLocation, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                This location will be created for <strong>{formData.entity}</strong> entity
                {formData.company ? ' under the selected company' : ' without a specific company'}.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLocationDialog(false)}>Cancel</Button>
          <Button onClick={handleAddLocation} variant="contained" disabled={!newLocation.name || !newLocation.city || !newLocation.state}>
            Add Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Position Dialog */}
      <Dialog open={openPositionDialog} onClose={() => setOpenPositionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Position</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Position Title"
                fullWidth
                required
                value={newPosition.title}
                onChange={(e) => setNewPosition({ ...newPosition, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Department"
                fullWidth
                required
                value={newPosition.department}
                onChange={(e) => setNewPosition({ ...newPosition, department: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={newPosition.description}
                onChange={(e) => setNewPosition({ ...newPosition, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                This position will be created for <strong>{formData.entity}</strong> entity
                {formData.company ? ' under the selected company' : ' without a specific company'}.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPositionDialog(false)}>Cancel</Button>
          <Button onClick={handleAddPosition} variant="contained" disabled={!newPosition.title || !newPosition.department}>
            Add Position
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Jobs;
